package org.example.zone360.pages.dashboard.recentAssessment

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.example.zone360.components.AudioProgressSliderCustom
import org.example.zone360.components.CustomSpacer
import org.example.zone360.components.ProgressIndicator
import org.example.zone360.components.SearchBar
import org.example.zone360.components.formatSessionDate
import org.example.zone360.components.formatSessionHours
import org.example.zone360.components.formatSessionHoursAmPm
import org.example.zone360.components.formatSessionMinutes
import org.example.zone360.models.SessionData
import org.example.zone360.pages.dashboard.SectionTitle
import org.example.zone360.theme.toSitePalette
import org.example.zone360.utils.mediaManagerUtils.AudioManager
import org.example.zone360.utils.Constants
import org.example.zone360.utils.Res
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.w3c.dom.Audio
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL

@Composable
fun AssessmentSessionItem(
    data: List<SessionData>,
    breakpoint: Breakpoint
) {
    val sitePalette = ColorMode.current.toSitePalette()

    var currentlyPlayingId by remember { mutableStateOf<String?>(null) }
    var loadingSessionId by remember { mutableStateOf<String?>(null) }
    val currentAudioRef = remember { mutableStateOf<Audio?>(null) }

    Column(Modifier.fillMaxSize()) {
        data.forEach { sessionData ->
            val sessionId = sessionData.sessionId
            var isDownloading by remember(sessionId) { mutableStateOf(false) }

            val parts = sessionId.removeSuffix(".aac").split("_")
            val sessionName = parts.first()
            val timeStamp = parts.last()

            val audioUrl = "${Res.Audio.audio_base_url}$sessionId"
            val audio = remember(sessionId) { mutableStateOf<Audio?>(null) }
            var audioProgress by remember(sessionId) { mutableStateOf(0f) }

            // Re-runs when audio.value is set (after play is clicked)
            DisposableEffect(audio.value) {
                val currentAudio = audio.value

                if (currentAudio == null) {
                    onDispose {}
                } else {

                    val timeUpdateListener: (org.w3c.dom.events.Event) -> Unit = {
                        val d = currentAudio.duration
                        if (!d.isNaN() && d > 0) {
                            audioProgress = (currentAudio.currentTime / d).toFloat()
                        }
                    }
                    val endedListener: (org.w3c.dom.events.Event) -> Unit = {
                        audioProgress = 0f
                        currentlyPlayingId = null
                    }

                    currentAudio.addEventListener("timeupdate", timeUpdateListener)
                    currentAudio.addEventListener("ended", endedListener)

                    onDispose {
                        currentAudio.pause()
                        currentAudio.removeEventListener("timeupdate", timeUpdateListener)
                        currentAudio.removeEventListener("ended", endedListener)
                    }
                }
            }

            Column(
                modifier = Modifier.fillMaxSize().padding(5.px)
            ) {
                SpanText(
                    "${sessionData.playerName}'s assessment".replaceFirstChar {
                        if (it.isLowerCase()) it.titlecase() else it.toString()
                    },
                    modifier = Modifier.fontFamily(Constants.FONT_FAMILY).fontWeight(500).fontSize(16.px)
                )
                if (breakpoint < Breakpoint.MD) {
                    SpanText(
                        sessionData.coachName,
                        modifier = Modifier.fontFamily(Constants.FONT_FAMILY)
                            .color(sitePalette.textFieldSupportiveTextColor.rgb)
                            .fontSize(13.px).margin(bottom = 3.px)
                    )
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                )
                {
                    if (breakpoint >= Breakpoint.MD) {
                        SpanText(
                            sessionData.coachName,
                            modifier = Modifier.fontFamily(Constants.FONT_FAMILY).width(110.px)
                                .fontWeight(400).fontSize(13.px)
                        )
                    }

                    Row(modifier = Modifier.width(100.px), verticalAlignment = Alignment.CenterVertically) {
                        Image(src = Res.Images.timeCircle, modifier = Modifier.size(17.px))
                        CustomSpacer(5.px)
                        SpanText(
                            sessionData.recordTime.toLongOrNull()?.let { (it / 1000).formatSessionMinutes() } ?: "-",
                            modifier = Modifier.fontFamily(Constants.FONT_FAMILY).fontWeight(400).margin(right = 3.px).fontSize(13.px)
                        )
                    }

                    Row(modifier = Modifier.width(180.px), verticalAlignment = Alignment.CenterVertically) {
                        Image(src = Res.Images.calender, modifier = Modifier.size(17.px))
                        CustomSpacer(5.px)
                        SpanText(
                            "${timeStamp.formatSessionDate()} ${timeStamp.formatSessionHoursAmPm()}",
                            modifier = Modifier.fontFamily(Constants.FONT_FAMILY).fontWeight(400).fontSize(13.px)
                        )
                    }

                    Row(verticalAlignment = Alignment.CenterVertically) {
                        CustomSpacer(6.px)
                        Box(contentAlignment = Alignment.Center) {
                            if (isDownloading) {
                                ProgressIndicator(size = 20.px)
                            } else {
                                Image(
                                    src = Res.Images.downloadPrimary,
                                    modifier = Modifier.size(29.px).cursor(Cursor.Pointer)
                                        .onClick {
                                            isDownloading = true
                                            val url = "${Res.Audio.audio_base_url}$sessionId"
                                            window.fetch(url).then { response ->
                                                response.blob().then { blob ->
                                                    val objectUrl = URL.createObjectURL(blob)
                                                    val link = document.createElement("a") as HTMLAnchorElement
                                                    link.href = objectUrl
                                                    link.download = "${sessionName}_${timeStamp}.aac"
                                                    document.body?.appendChild(link)
                                                    link.click()
                                                    document.body?.removeChild(link)
                                                    URL.revokeObjectURL(objectUrl)
                                                    isDownloading = false
                                                }
                                            }
                                        }
                                )
                            }
                        }
                    }
                }

                Row(
                    modifier = Modifier.fillMaxWidth().margin(topBottom = 4.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    when {
                        loadingSessionId == sessionId -> {
                            Box(contentAlignment = Alignment.Center) {
                                Image(src = Res.Images.blueCircle, modifier = Modifier.size(30.px).borderRadius(100.px))
                                ProgressIndicator(10.px)
                            }
                        }

                        currentlyPlayingId == sessionId -> {
                            Image(
                                src = Res.Images.pausebutton,
                                modifier = Modifier.size(30.px).cursor(Cursor.Pointer)
                                    .onClick {
                                        audio.value?.pause()
                                        currentlyPlayingId = null
                                        audioProgress = 0f
                                    }
                            )
                        }

                        else -> {
                            Image(
                                src = Res.Images.playbutton,
                                modifier = Modifier.size(30.px).cursor(Cursor.Pointer)
                                    .onClick {
                                        // Pause whatever was playing before
                                        currentAudioRef.value?.pause()
                                        loadingSessionId = sessionId

                                        // Lazy create via AudioManager
                                        val prepared = AudioManager.prepare(audioUrl)
                                        audio.value = prepared
                                        currentAudioRef.value = prepared
                                        currentlyPlayingId = sessionId
                                        loadingSessionId = null

                                        if (prepared.readyState.toInt() < 2) {
                                            prepared.load()
                                            prepared.onloadeddata = { prepared.play() }
                                        } else {
                                            prepared.play()
                                        }
                                    }
                            )
                        }
                    }

                    CustomSpacer(10.px)

                    AudioProgressSliderCustom(
                        progress = audioProgress,
                        onProgressChange = { newProgress ->
                            val currentAudio = audio.value ?: return@AudioProgressSliderCustom
                            audioProgress = newProgress
                            val dur = currentAudio.duration
                            if (dur > 0) {
                                currentAudio.currentTime = dur * newProgress
                                if (currentlyPlayingId == sessionId && currentAudio.paused) {
                                    currentAudio.play()
                                }
                            }
                        }
                    )
                }

                HorizontalDivider(modifier = Modifier.color(Colors.Gray).fillMaxWidth())
            }
        }
    }
}